/**
 * Générateur — Connaître et appliquer la technique de la division décimale
 * Attendu ID : 616 | Niveau : 6ème | Domaine : NC (Nombres et calculs)
 *
 * Ce générateur contient 4 exercices didactiquement distincts. Chacun teste
 * une stratégie cognitive différente de l'élève (cf. analyse pédagogique).
 *
 *   1. `quotient_calc`         → QCM unique + Saisie
 *      Technique pure de la division décimale (D ÷ d, d entier < 10).
 *      Inclut des cas avec d=2 reformulés en "moitié de D" (vocabulaire).
 *
 *   2. `prix_unit_direct`      → QCM unique + Saisie
 *      Modélisation d'un problème 1 étape : prix total ÷ quantité.
 *
 *   3. `vrai_faux_divisions`   → Vrai/Faux
 *      Stratégie de vérification (multiplier le résultat par le diviseur).
 *      6 égalités, 3 vraies + 3 fausses (3 familles d'erreur).
 *
 *   4. `drag_cascade`          → Texte à trous (drag)
 *      Propagation d'un résultat dans une cascade de divisions.
 *
 * NB : les exercices "billet/rendu" (2 étapes) sont couverts par gen_617
 * (`billet_rendu_partage`), qui est dédié à la résolution de problèmes
 * composés. Les exercices "relier" et "moitié" ont été fusionnés (relier
 * était redondant avec quotient_calc, moitié est intégré comme variante
 * vocabulaire de quotient_calc avec d=2).
 *
 * Règles dures de la fiche cluster 9 (attendu 616) :
 *   - Diviseur TOUJOURS entier < 10 (technique posée, pas de calculatrice).
 *   - Quotients exacts à au plus 3 décimales (aucun reste non nul au centième/millième).
 *   - 3 familles d'erreur à instancier : oubli de virgule, virgule décalée, chiffre erroné.
 *   - Anti-équivalence numérique entre bonne réponse et distracteurs (filtre isEquivalent).
 *
 * Arithmétique : multiplication entière exacte (Number), aucune imprécision flottante.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou,
} from '../../../utils';

// ─── Constantes ───
const ATTENDU_ID = 616;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Rappel affiché en tête de tous les énoncés (revue 2026-08-24, choix prof,
// même principe que le gen 613) : la technique posée se travaille au brouillon.
const RAPPEL_BROUILLON = '<p class="italic text-slate-400">✏️ Pense à poser la division sur ton cahier ou au brouillon avant de répondre.</p>';

const L_QUOTIENT_QCM    = 'Calcul direct d\'un quotient décimal par un entier < 10 (QCM)';
const L_QUOTIENT_SAISIE = 'Calcul direct d\'un quotient décimal par un entier < 10 (saisie)';
const L_PRIX_DIRECT_QCM = 'Problème prix unitaire direct (QCM)';
const L_PRIX_DIRECT_SAI = 'Problème prix unitaire direct (saisie)';
const L_VF              = 'Détecter les divisions correctes parmi 3 pièges classiques';
const L_DRAG            = 'Cascade de divisions par un entier (drag)';

// ═══════════════════════════════════════════════════════════
// HELPERS ARITHMÉTIQUES (multiplication entière exacte, écriture française)
// Valide tant que les facteurs strippés restent ≪ Number.MAX_SAFE_INTEGER (~9×10¹⁵)
// ═══════════════════════════════════════════════════════════

const decCount = (s) => {
    const [, dec = ''] = String(s).split(',');
    return dec.length;
};

/** Normalise un entier scaled + nbDec → string française « 12,5 » (sans zéros finaux). */
const intToDecStr = (n, nbDec) => {
    const sign = n < 0 ? '-' : '';
    let s = String(Math.abs(n));
    if (nbDec === 0) return sign + s;
    while (s.length <= nbDec) s = '0' + s;
    const intPart = s.slice(0, s.length - nbDec).replace(/^0+/, '') || '0';
    const decPart = s.slice(s.length - nbDec).replace(/0+$/, '');
    return decPart === '' ? sign + intPart : `${sign}${intPart},${decPart}`;
};

/** Produit exact a × b (strings). */
const mulDec = (a, b) => {
    const da = decCount(a);
    const db = decCount(b);
    const ai = parseInt(String(a).replace(/[\s,]/g, ''), 10);
    const bi = parseInt(String(b).replace(/[\s,]/g, ''), 10);
    return intToDecStr(ai * bi, da + db);
};

/** Somme exacte a + b (strings). */
const addDec = (a, b) => {
    const da = decCount(a);
    const db = decCount(b);
    const D = Math.max(da, db);
    const ai = parseInt(String(a).replace(/[\s,]/g, '') + '0'.repeat(D - da), 10);
    const bi = parseInt(String(b).replace(/[\s,]/g, '') + '0'.repeat(D - db), 10);
    return intToDecStr(ai + bi, D);
};

/**
 * Division exacte d'un décimal D par un entier d, avec au plus maxDec décimales.
 * Retourne une string française, ou null si la division ne tombe pas juste à maxDec décimales.
 */
const divDec = (D, d, maxDec = 3) => {
    const dInt = parseInt(String(d), 10);
    if (dInt <= 0) return null;
    const da = decCount(D);
    const shift = maxDec - da;
    if (shift < 0) return null;
    const Di = parseInt(String(D).replace(/[\s,]/g, ''), 10) * Math.pow(10, shift);
    if (Di % dInt !== 0) return null;
    return intToDecStr(Di / dInt, maxDec);
};

/**
 * Décale la virgule de k rangs vers la gauche (÷10^k).
 * Ex : shiftLeft('45,6', 1) = '4,56' ; shiftLeft('7', 1) = '0,7'.
 */
const shiftLeft = (numStr, k) => {
    const clean = String(numStr).replace(/\s/g, '').replace('-', '');
    const [intPart, decPart = ''] = clean.split(',');
    const allDigits = intPart + decPart;
    const newDecs = decPart.length + k;
    if (newDecs < allDigits.length) {
        const newInt = allDigits.slice(0, allDigits.length - newDecs).replace(/^0+/, '') || '0';
        const newDec = allDigits.slice(allDigits.length - newDecs).replace(/0+$/, '');
        return newDec === '' ? newInt : `${newInt},${newDec}`;
    }
    const zeros = '0'.repeat(newDecs - allDigits.length);
    const rawDec = zeros + allDigits;
    const decTrim = rawDec.replace(/0+$/, '');
    return decTrim === '' ? '0' : `0,${decTrim}`;
};

/**
 * Décale la virgule de k rangs vers la droite (×10^k).
 * Ex : shiftRight('4,56', 1) = '45,6'.
 */
const shiftRight = (numStr, k) => {
    const clean = String(numStr).replace(/\s/g, '').replace('-', '');
    const [intPart, decPart = ''] = clean.split(',');
    const allDigits = intPart + decPart;
    const oldDecs = decPart.length;
    const newDecs = oldDecs - k;
    if (newDecs >= 0) {
        const cutAt = allDigits.length - newDecs;
        const newInt = (allDigits.slice(0, cutAt) || '0').replace(/^0+/, '') || '0';
        const newDec = allDigits.slice(cutAt).replace(/0+$/, '');
        return newDec === '' ? newInt : `${newInt},${newDec}`;
    }
    return (allDigits + '0'.repeat(-newDecs)).replace(/^0+/, '') || '0';
};

/** Compare deux décimaux numériquement (tolérance 1e-9). */
const isEquivalent = (a, b) => {
    const f = (s) => parseFloat(String(s).replace(',', '.').replace(/[^\d.\-]/g, ''));
    return Math.abs(f(a) - f(b)) < 1e-9;
};

/** Écriture canonique (revue 2026-08-21, même règle que le gen 615) : retirer
 *  les zéros de fin (« 0,90 » → « 0,9 ») pour que tous les choix partagent la
 *  même convention. Les prix des ÉNONCÉS (pool) restent affichés tels quels. */
const formatEur2 = (s) => {
    const str = String(s);
    if (!str.includes(',')) return str;
    let t = str.replace(/0+$/, '');
    if (t.endsWith(',')) t = t.slice(0, -1);
    return t;
};

/**
 * Nettoie une liste de distracteurs : retire les doublons et ceux équivalents
 * à la bonne réponse, complète via `fallbackFn` si besoin (jusqu'à 20 essais).
 */
const cleanDistracteurs = (bonne, dists, fallbackFn) => {
    const out = [];
    const seen = [bonne];
    const eq = (x) => seen.some((y) => isEquivalent(x, y));
    for (const d of dists) {
        if (!eq(d)) { out.push(d); seen.push(d); }
    }
    let guard = 0;
    while (out.length < 3 && guard < 20) {
        const cand = fallbackFn(guard);
        if (!eq(cand)) { out.push(cand); seen.push(cand); }
        guard++;
    }
    return out.slice(0, 3);
};

// ═══════════════════════════════════════════════════════════
// POOLS DE CONTEXTES
// ═══════════════════════════════════════════════════════════

const PRENOMS_F = ['Léa', 'Zoé', 'Maya', 'Chloé', 'Jade', 'Emma', 'Lila', 'Nour', 'Anna', 'Louise', 'Sarah', 'Camille', 'Diane'];
const PRENOMS_M = ['Noé', 'Hugo', 'Tom', 'Yanis', 'Adam', 'Raphaël', 'Lucas', 'Kévin', 'Max', 'Paul'];
const pickPrenom = () => {
    const fem = Math.random() < 0.5;
    return { nom: randomChoice(fem ? PRENOMS_F : PRENOMS_M), fem };
};

/** Triplets (D, d, q) pour quotient_calc : D ÷ d = q exact, d ∈ [2,9].
 *  Inclut des cas avec d=2 qui peuvent être reformulés en "moitié de D". */
const POOL_QUOTIENT_CALC = [
    // Cas d > 2 : quotients "classiques"
    ['78,296', 8, '9,787'], ['165,75', 3, '55,25'], ['45,6', 8, '5,7'],
    ['45,6', 3, '15,2'], ['45,6', 4, '11,4'],
    ['12,5', 5, '2,5'], ['7,2', 4, '1,8'],
    ['31,5', 5, '6,3'], ['6,4', 4, '1,6'], ['8,4', 6, '1,4'],
    ['26,25', 5, '5,25'], ['36,9', 3, '12,3'], ['18,75', 3, '6,25'],
    ['23,1', 7, '3,3'], ['15,6', 4, '3,9'], ['56,7', 9, '6,3'],
    ['13,44', 6, '2,24'], ['21,6', 8, '2,7'], ['4,8', 6, '0,8'],
    ['92,4', 4, '23,1'], ['18,9', 9, '2,1'], ['38,5', 5, '7,7'],
    ['73,8', 6, '12,3'], ['59,85', 7, '8,55'], ['27,6', 4, '6,9'],
    ['12,6', 3, '4,2'], ['10,8', 8, '1,35'], ['5,76', 4, '1,44'],
    ['219', 4, '54,75'], ['33', 4, '8,25'], ['51', 4, '12,75'],
    ['17,5', 5, '3,5'], ['38,4', 8, '4,8'], ['64,8', 9, '7,2'],
    // Cas d = 2 : quotients qui seront parfois reformulés en "moitié de D"
    ['45,6', 2, '22,8'], ['9,5', 2, '4,75'], ['7,5', 2, '3,75'],
    ['12,5', 2, '6,25'], ['8,5', 2, '4,25'], ['15,5', 2, '7,75'],
    ['6,5', 2, '3,25'], ['10,5', 2, '5,25'], ['14,5', 2, '7,25'],
    ['18,5', 2, '9,25'], ['22,5', 2, '11,25'], ['13,5', 2, '6,75'],
    ['5,5', 2, '2,75'], ['3,5', 2, '1,75'], ['11,5', 2, '5,75'],
    ['17,5', 2, '8,75'], ['4,5', 2, '2,25'], ['16,5', 2, '8,25'],
    ['19,5', 2, '9,75'], ['21,5', 2, '10,75'], ['24,5', 2, '12,25'],
];

/** Pool prix unit direct : (nArticles ∈ [2,9], prixTotal, prixUnit exact). */
const POOL_PRIX_UNIT = [
    { n: 6, total: '5,40', unit: '0,90' },  // Noé 6 croissants
    { n: 5, total: '8,75', unit: '1,75' },  // Zoé 5 cahiers
    { n: 4, total: '7,20', unit: '1,80' },
    { n: 3, total: '2,70', unit: '0,90' },
    { n: 8, total: '10,40', unit: '1,30' },
    { n: 6, total: '11,70', unit: '1,95' },
    { n: 5, total: '6,25', unit: '1,25' },
    { n: 4, total: '10,20', unit: '2,55' },
    { n: 7, total: '5,60', unit: '0,80' },
    { n: 3, total: '15,60', unit: '5,20' },
    { n: 5, total: '17,50', unit: '3,50' },
    { n: 6, total: '9,30', unit: '1,55' },
    { n: 4, total: '13,00', unit: '3,25' },
    { n: 8, total: '14,40', unit: '1,80' },
    { n: 5, total: '12,75', unit: '2,55' },
    { n: 9, total: '13,50', unit: '1,50' },
    { n: 4, total: '6,80', unit: '1,70' },
    { n: 6, total: '21,60', unit: '3,60' },
    { n: 3, total: '8,70', unit: '2,90' },
    { n: 7, total: '9,10', unit: '1,30' },
];
const ARTICLES_UNIT = [
    { sing: 'croissant', plu: 'croissants' },
    { sing: 'cahier',    plu: 'cahiers' },
    { sing: 'pain',      plu: 'pains' },
    { sing: 'gâteau',    plu: 'gâteaux' },
    { sing: 'yaourt',    plu: 'yaourts' },
    { sing: 'stylo',     plu: 'stylos' },
    { sing: 'crayon',    plu: 'crayons' },
    { sing: 'livre',     plu: 'livres' },
    { sing: 'classeur',  plu: 'classeurs' },
    { sing: 'biscuit',   plu: 'biscuits' },
    { sing: 'jus',       plu: 'jus' },
    { sing: 'paquet de chips', plu: 'paquets de chips' },
];

// ═══════════════════════════════════════════════════════════
// EXERCICE 1 — quotient_calc (QCM + Saisie)
// ═══════════════════════════════════════════════════════════

const pickQuotientTriple = () => {
    let triple = randomChoice(POOL_QUOTIENT_CALC);
    // Sanity check au cas où : recalcul exact
    const [D, d, q] = triple;
    const qCheck = divDec(D, d, 3);
    if (qCheck !== q) {
        // fallback déterministe
        triple = ['45,6', 8, '5,7'];
    }
    return { D: triple[0], d: triple[1], q: triple[2] };
};

/**
 * Contextes NON marchands pour habiller D ÷ d — diversifie l'exercice 1 au-delà
 * du simple « Calcule : D ÷ d = ? » et du contexte commercial dominant.
 * Chaque contexte renvoie une consigne HTML qui intègre D (avec unité) et d.
 * Tous les contextes sont neutres arithmétiquement : la réponse reste q,
 * seule la formulation change.
 */
const CONTEXTES_QUOTIENT = [
    // 1. Abstrait (forme canonique historique)
    (D, d, { prenom }) => `<p>Calcule : ${D} ÷ ${d} = ?</p>`,
    (D, d, { prenom }) => `<p>Quel est le quotient de ${D} par ${d} ?</p>`,
    // 2. Partage de longueur de fil (géométrie / travaux manuels)
    (D, d, { prenom, fem }) =>
        `<p>${prenom} possède un fil de ${D} m qu${fem ? "'elle" : "'il"} partage en ${d} morceaux de même longueur. Quelle est la longueur d'un morceau (en m) ?</p>`,
    // 3. Partage d'une durée (temps, sciences)
    (D, d, { prenom, fem }) =>
        `<p>${prenom} regarde un film de ${D} minutes en ${d} soirées de même durée. Combien de minutes regarde-t-${fem ? 'elle' : 'il'} chaque soirée ?</p>`,
    // 4. Partage d'une masse (sciences / cuisine collective)
    (D, d, { prenom, fem }) =>
        `<p>${prenom} verse ${D} kg de graines dans ${d} sachets identiques. Quelle est la masse (en kg) d'un sachet ?</p>`,
    // 5. Partage d'un volume (sciences / arrosage)
    (D, d, { prenom, fem }) =>
        `<p>${prenom} remplit ${d} arrosoirs identiques avec ${D} L d'eau au total. Quel volume (en L) contient un arrosoir ?</p>`,
    // 6. Partage d'une distance de course (sport)
    (D, d, { prenom, fem }) =>
        `<p>Lors d'un relais, l'équipe de ${prenom} parcourt ${D} km en ${d} relais égaux. Quelle distance (en km) parcourt chaque coureur ?</p>`,
];

/** Formulations spécifiques au cas d=2 qui introduisent le vocabulaire "moitié".
 *  Activées avec 50% de probabilité quand le diviseur est 2 pour diversifier
 *  l'exercice tout en restant mathématiquement identique à une division par 2. */
const CONTEXTES_MOITIE = [
    (D) => `<p>Quelle est la moitié de ${D} ?</p>`,
    (D) => `<p>Calcule la moitié de ${D}.</p>`,
    (D) => `<p>Trouve la moitié du nombre ${D}.</p>`,
];

const buildEnonceQuotient = (D, d) => {
    // Si d === 2, 50% de chance d'activer le vocabulaire "moitié"
    if (d === 2 && Math.random() < 0.5) {
        return RAPPEL_BROUILLON + randomChoice(CONTEXTES_MOITIE)(D);
    }
    const { nom: prenom, fem } = pickPrenom();
    // Vraisemblance (revue 2026-08-21) : les habillages concrets n'acceptent
    // que des valeurs réalistes — ≤ 2 décimales pour fil/masse/volume/distance,
    // ≤ 1 décimale pour les durées en minutes. Au-delà (« un fil de 78,296 m »,
    // « un film de 165,75 minutes »), on reste sur les formulations abstraites
    // (index 0-1 du pool de contextes).
    const nbDec = decCount(D);
    let pool = CONTEXTES_QUOTIENT;
    if (nbDec >= 3) {
        pool = CONTEXTES_QUOTIENT.slice(0, 2);
    } else if (nbDec === 2) {
        pool = CONTEXTES_QUOTIENT.filter((_, i) => i !== 3); // exclut la durée
    }
    const tmpl = randomChoice(pool);
    return RAPPEL_BROUILLON + tmpl(D, d, { prenom, fem });
};

// Anti-répétition immédiate (revue 2026-08-21, recette standard).
let derniereSignatureQcm616 = null;

const genQuotientCalcQcm = () => {
    let payload = construireQuotientCalcQcm();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureQcm616; t++) {
        payload = construireQuotientCalcQcm();
    }
    derniereSignatureQcm616 = payload.texte;
    return payload;
};

const construireQuotientCalcQcm = () => {
    const { D, d, q } = pickQuotientTriple();

    // Distracteurs pour QCM direct (familles fiche §4 616) :
    //  - OUBLI DE VIRGULE : quotient sans virgule (seulement partie entière)
    //  - VIRGULE DÉCALÉE (÷10)
    //  - MULTIPLICATION : D × d (erreur d'opération)
    const [qInt, qDec = ''] = q.split(',');
    // Quotient < 1 (revue 2026-08-21, arbitrage prof) : deux misconceptions
    // plausibles, tirées 50/50 — « 0 » (troncature : « 6 ne rentre pas dans 4 »)
    // ou les chiffres sans virgule (oubli de virgule : 48 ÷ 6 = 8).
    const distOubli = (qInt === '0' && randInt(0, 1))
        ? String(parseInt(q.replace(',', ''), 10))
        : qInt; // partie entière seule (troncature)
    const distShift = shiftLeft(q, 1); // virgule décalée ÷10
    const distMul = mulDec(D, String(d));

    const distracteurs = cleanDistracteurs(q, [distOubli, distShift, distMul], (i) => {
        // fallback : quotient * 10 (si pas déjà pris)
        if (i === 0) return shiftRight(q, 1);
        if (i === 1) return String(parseInt(qInt, 10) + 1) + (qDec ? `,${qDec}` : '');
        return String(parseInt(qInt, 10) * 2);
    });

    const choix = [q, ...distracteurs];
    const payload = makeQcm(
        choix, q,
        buildEnonceQuotient(D, d),
        opts(L_QUOTIENT_QCM),
    );
    payload._meta.generatorKey = 'quotient_calc';
    return payload;
};

// Anti-répétition immédiate (revue 2026-08-21, recette standard).
let derniereSignatureSaisie616 = null;

const genQuotientCalcSaisie = () => {
    let payload = construireQuotientCalcSaisie();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureSaisie616; t++) {
        payload = construireQuotientCalcSaisie();
    }
    derniereSignatureSaisie616 = payload.texte;
    return payload;
};

const construireQuotientCalcSaisie = () => {
    const { D, d, q } = pickQuotientTriple();
    const payload = makeSaisie(
        q,
        buildEnonceQuotient(D, d),
        { ...opts(L_QUOTIENT_SAISIE), strict: false, suffixe: '' },
    );
    payload._meta.generatorKey = 'quotient_calc';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 3 — prix_unit_direct (QCM + Saisie)
// ═══════════════════════════════════════════════════════════

const pickPrixUnitCtx = () => {
    const item = randomChoice(ARTICLES_UNIT);
    const pool = randomChoice(POOL_PRIX_UNIT);
    const { nom: prenom, fem } = pickPrenom();
    // Vérification arithmétique : n * unit == total
    const prodCheck = mulDec(pool.unit, String(pool.n));
    if (!isEquivalent(prodCheck, pool.total)) {
        // fallback sûr
        return {
            prenom, fem, item, n: 6, total: '5,40', unit: '0,90',
        };
    }
    return { prenom, fem, item, n: pool.n, total: pool.total, unit: pool.unit };
};

const buildEnoncePrixUnit = (ctx) => {
    const { prenom, fem, item, n, total } = ctx;
    const pronom = fem ? 'Elle' : 'Il';
    const formulations = [
        `<p>${prenom} achète ${n} ${item.plu} identiques. ${pronom} paie ${total} €. Quel est le prix d'un ${item.sing} ?</p>`,
        `<p>${prenom} paie ${total} € pour ${n} ${item.plu} identiques. Combien coûte un ${item.sing} ?</p>`,
        `<p>${n} ${item.plu} identiques coûtent ${total} €. Quel est le prix d'un ${item.sing} ?</p>`,
    ];
    return RAPPEL_BROUILLON + randomChoice(formulations);
};

// Anti-répétition immédiate (revue 2026-08-21, recette standard).
let derniereSignaturePrixQcm616 = null;

const genPrixUnitDirectQcm = () => {
    let payload = construirePrixUnitDirectQcm();
    for (let t = 0; t < 8 && payload.texte === derniereSignaturePrixQcm616; t++) {
        payload = construirePrixUnitDirectQcm();
    }
    derniereSignaturePrixQcm616 = payload.texte;
    return payload;
};

const construirePrixUnitDirectQcm = () => {
    const ctx = pickPrixUnitCtx();
    const { n, total, unit } = ctx;
    // Distracteurs fiche §4 616 exercice C (revue 2026-08-21) :
    //  - MULTIPLICATION au lieu de ÷ : total × n
    //  - VIRGULE DÉCALÉE : unit × 10 = shiftRight(unit, 1)
    //  - CHIFFRE ERRONÉ : dernière décimale du prix unitaire modifiée, MÊME
    //    gabarit que la bonne — sans lui, la bonne était le seul choix à
    //    2 décimales dans 18,7 % des tirages (« le prix avec des centimes »).
    //    Remplace « total + n », misconception faible.
    const distShift = shiftRight(unit, 1) + ' €';
    const chiffreErrone = (() => {
        const [ip, dp = ''] = String(unit).split(',');
        if (!dp) return addDec(unit, '1');
        const last = parseInt(dp.slice(-1), 10);
        const delta = randomChoice([3, -3, 4, -4].filter((x) => last + x >= 1 && last + x <= 9));
        return `${ip},${dp.slice(0, -1)}${last + delta}`;
    })();

    const bonne = formatEur2(unit) + ' €';
    const distracteurs = cleanDistracteurs(
        bonne,
        [formatEur2(mulDec(total, String(n))) + ' €', distShift, formatEur2(chiffreErrone) + ' €'],
        (i) => {
            if (i === 0) return shiftLeft(unit, 1) + ' €';
            if (i === 1) return formatEur2(total) + ' €';
            return formatEur2(addDec(unit, String(n))) + ' €';
        },
    );

    const choix = [bonne, ...distracteurs];
    const payload = makeQcm(
        choix, bonne,
        buildEnoncePrixUnit(ctx),
        opts(L_PRIX_DIRECT_QCM),
    );
    payload._meta.generatorKey = 'prix_unit_direct';
    return payload;
};

// Anti-répétition immédiate (revue 2026-08-22, recette standard).
let derniereSignaturePrixSaisie616 = null;

const genPrixUnitDirectSaisie = () => {
    let payload = construirePrixUnitDirectSaisie();
    for (let t = 0; t < 8 && payload.texte === derniereSignaturePrixSaisie616; t++) {
        payload = construirePrixUnitDirectSaisie();
    }
    derniereSignaturePrixSaisie616 = payload.texte;
    return payload;
};

const construirePrixUnitDirectSaisie = () => {
    const ctx = pickPrixUnitCtx();
    const payload = makeSaisie(
        ctx.unit,
        buildEnoncePrixUnit(ctx),
        { ...opts(L_PRIX_DIRECT_SAI), strict: false, suffixe: '€' },
    );
    payload._meta.generatorKey = 'prix_unit_direct';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 4 — vrai_faux_divisions (V/F uniquement)
// ═══════════════════════════════════════════════════════════

/** Génère une ligne V/F exacte (vraie) à partir du pool quotient_calc.
 *  `couples` (revue 2026-08-22) : unicité du couple (D, d) entre les 6 lignes —
 *  la même division apparaissait deux fois (une vraie, une fausse) dans 19 %
 *  des tirages, et l'une se déduisait de l'autre. */
const makeLigneVraie = (used, couples) => {
    for (let t = 0; t < 20; t++) {
        const [D, d, q] = randomChoice(POOL_QUOTIENT_CALC);
        const qCheck = divDec(D, d, 3);
        if (qCheck !== q) continue;
        if (couples.has(`${D}|${d}`)) continue;
        const text = `${D} ÷ ${d} = ${q}`;
        if (!used.has(text)) { used.add(text); couples.add(`${D}|${d}`); return text; }
    }
    return '9,5 ÷ 2 = 4,75';
};

/** Génère une ligne V/F fausse via 3 familles d'erreur tournantes. */
const makeLigneFausse = (used, couples, famille) => {
    for (let t = 0; t < 20; t++) {
        const [D, d, q] = randomChoice(POOL_QUOTIENT_CALC);
        const qCheck = divDec(D, d, 3);
        if (qCheck !== q) continue;
        if (couples.has(`${D}|${d}`)) continue;
        let faux;
        if (famille === 'oubli_virgule') {
            // quotient tronqué à la partie entière
            const [qi] = q.split(',');
            faux = qi;
        } else if (famille === 'virgule_decalee') {
            faux = shiftRight(q, 1); // ×10
        } else {
            // chiffre_errone : dernière décimale modifiée
            const digits = q.replace(',', '');
            if (digits.length < 2) continue;
            const last = digits.slice(-1);
            const newLast = String((parseInt(last, 10) + 5) % 10);
            const [intPart, decPart = ''] = q.split(',');
            if (!decPart) continue;
            const newDec = decPart.slice(0, -1) + newLast;
            faux = `${intPart},${newDec}`;
        }
        if (isEquivalent(faux, q)) continue;
        const text = `${D} ÷ ${d} = ${faux}`;
        if (!used.has(text)) { used.add(text); couples.add(`${D}|${d}`); return text; }
    }
    return `165,75 ÷ 3 = 55`; // fallback (oubli virgule)
};

const genVraiFauxDivisionsTableau = () => {
    const used = new Set();
    const couples = new Set();
    // Quota variable (revue 2026-08-22) : 2 à 4 vraies sur 6 lignes, pour
    // empêcher la stratégie de comptage « toujours 3 V / 3 F ».
    const nbVraies = randInt(2, 4);
    const famillesPool = shuffle(['oubli_virgule', 'virgule_decalee', 'chiffre_errone']);
    const familles = [];
    for (let k = 0; k < 6 - nbVraies; k++) familles.push(famillesPool[k % famillesPool.length]);

    const lignesVraies = [];
    for (let k = 0; k < nbVraies; k++) lignesVraies.push(makeLigneVraie(used, couples));
    const lignesFausses = familles.map((f) => makeLigneFausse(used, couples, f));

    // Mélanger vraies et fausses
    const indexed = [
        ...lignesVraies.map((l) => ({ l, br: 0 })),  // 0 = Vraie
        ...lignesFausses.map((l) => ({ l, br: 1 })), // 1 = Fausse
    ];
    const shuf = shuffle(indexed);

    const payload = makeVraiFaux(
        shuf.map((x) => x.l),
        shuf.map((x) => x.br),
        // Revue 2026-08-24 (choix prof) : sur le V/F, le bon geste est la
        // PREUVE PAR LA MULTIPLICATION (dividende = diviseur × quotient),
        // pas de reposer la division.
        `<p class="italic text-slate-400">✏️ Pense à vérifier chaque égalité par une multiplication au brouillon.</p><p>Pour chaque égalité, dis si elle est vraie ou fausse.</p>`,
        { ...opts(L_VF), colonnes: ['Vraie', 'Fausse'] },
    );
    payload._meta.generatorKey = 'vrai_faux_divisions';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 7 — drag_cascade (Drag uniquement)
// ═══════════════════════════════════════════════════════════

/** Cascades pré-vérifiées (conservées comme fallback). */
const POOL_DRAG_CASCADE = [
    { d: 4, triples: [['15', '3,75'], ['19', '4,75'], ['27', '6,75']] },
    { d: 4, triples: [['13', '3,25'], ['21', '5,25'], ['29', '7,25']] },
    { d: 5, triples: [['8,5', '1,7'], ['12,5', '2,5'], ['17,5', '3,5']] },
    { d: 2, triples: [['9,5', '4,75'], ['7,5', '3,75'], ['11,5', '5,75']] },
    { d: 8, triples: [['4,8', '0,6'], ['9,6', '1,2'], ['12', '1,5']] },
    { d: 3, triples: [['6,9', '2,3'], ['12,6', '4,2'], ['18,3', '6,1']] },
    { d: 6, triples: [['8,4', '1,4'], ['12,6', '2,1'], ['25,2', '4,2']] },
];

/** Quotients "canoniques" reconnus instantanément par un élève 6ème. */
const QUOTIENTS_CANONIQUES = [
    // Entiers simples
    '1', '2', '3', '4', '5', '6', '7',
    // 1 décimale "ronde"
    '0,2', '0,4', '0,5', '0,6', '0,8',
    '1,5', '2,5', '3,5', '4,5', '5,5',
    // Quarts (2 décimales)
    '0,25', '0,75', '1,25', '1,75', '2,25', '2,75', '3,25', '3,75',
    // Autres décimales pédagogiques
    '0,15', '0,35', '0,45', '0,65', '0,85',
];

/**
 * Tire un quotient fini : 50% canoniques (pédagogiquement familiers), 50% random.
 * Retourne { q, qScaled, qDec } où qScaled = q × 100 (entier) et qDec ∈ [0, 2].
 */
const pickQuotientFini = () => {
    if (Math.random() < 0.5) {
        const q = randomChoice(QUOTIENTS_CANONIQUES);
        const [ip, dp = ''] = q.split(',');
        const qDec = dp.length;
        const qScaled = parseInt(ip) * Math.pow(10, qDec) + (dp ? parseInt(dp) : 0);
        return { q, qScaled, qDec };
    }
    // Random : qInt ∈ [1, 7], qDec ∈ {0, 1, 2}
    const qDec = randInt(0, 2);
    const qInt = randInt(1, 7);
    const qFrac = qDec === 0 ? 0 : randInt(1, Math.pow(10, qDec) - 1);
    const qScaled = qInt * Math.pow(10, qDec) + qFrac;
    const q = qDec === 0 ? `${qInt}` : `${qInt},${String(qFrac).padStart(qDec, '0')}`;
    return { q, qScaled, qDec };
};

/**
 * Génère dynamiquement une cascade {d, triples[3]} avec quotients finis garantis
 * (construction q → D = q × d). Retourne null si échec (rare).
 */
const genCascadeDynamique = () => {
    const d = randInt(2, 9);
    const triples = [];
    const seenQs = new Set();
    const seenDs = new Set();
    for (let attempts = 0; attempts < 50 && triples.length < 3; attempts++) {
        const { q, qScaled, qDec } = pickQuotientFini();
        if (seenQs.has(q)) continue;
        // D = q × d (en scaled)
        const DScaled = qScaled * d;
        const Dint = Math.floor(DScaled / Math.pow(10, qDec));
        const Dfrac = DScaled % Math.pow(10, qDec);
        const DfracTrimmed = qDec === 0 ? '' : String(Dfrac).padStart(qDec, '0').replace(/0+$/, '');
        const D = DfracTrimmed ? `${Dint},${DfracTrimmed}` : String(Dint);
        if (seenDs.has(D)) continue;
        seenQs.add(q);
        seenDs.add(D);
        triples.push([D, q]);
    }
    return triples.length >= 3 ? { d, triples } : null;
};

// Anti-répétition immédiate (revue 2026-08-22, recette standard).
let derniereSignatureDrag616 = null;

const genDragCascadeTrou = () => {
    let payload = construireDragCascadeTrou();
    for (let t = 0; t < 8 && payload.contenu.phrase === derniereSignatureDrag616; t++) {
        payload = construireDragCascadeTrou();
    }
    derniereSignatureDrag616 = payload.contenu.phrase;
    return payload;
};

const construireDragCascadeTrou = () => {
    // 100% dynamique : ~100k+ cascades possibles (mix canoniques/random)
    const cascade = genCascadeDynamique() || randomChoice(POOL_DRAG_CASCADE);
    const { d, triples } = cascade;

    // Vérification des quotients
    const verified = triples.filter(([D, q]) => {
        const c = divDec(D, d, 3);
        return c === q;
    });
    if (verified.length < 3) {
        // Fallback
        return buildDragCascadeFallback();
    }

    // Construire la phrase à trous (une équation par ligne)
    const phrase = verified.slice(0, 3)
        .map(([D, q]) => `${D} ÷ ${d} = {{${q}}}`)
        .join('\n');

    // Distracteurs (fiche §6 exercice G) :
    //  P1 = shiftLeft(q1, 1) virgule × 10
    //  P2 = shiftRight(q2, 1) virgule ÷ 10
    //  P3 = permutation des 2 dernières décimales de q3
    const [q1, q2, q3] = verified.slice(0, 3).map(([, q]) => q);
    const p1 = shiftRight(q1, 1); // déplacement × 10 (virgule trop à droite)
    const p2 = shiftLeft(q2, 1);  // virgule décalée ÷ 10
    // Permutation des 2 dernières décimales
    const permuteDerniers = (q) => {
        if (!q.includes(',')) return shiftRight(q, 1);
        const [ip, dp] = q.split(',');
        if (dp.length < 2) return `${ip},${dp}5`;
        const swapped = dp.slice(0, -2) + dp[dp.length - 1] + dp[dp.length - 2];
        return `${ip},${swapped}`;
    };
    const p3 = permuteDerniers(q3);

    const bonnes = [q1, q2, q3];
    const dist = cleanDistracteurs('__sentinel__', [p1, p2, p3], (i) => {
        if (i === 0) return shiftRight(q1, 2);
        if (i === 1) return shiftLeft(q2, 2);
        return `${q3}0`;
    });
    // Filtre : retirer les pièges équivalents à une bonne réponse
    const dist2 = dist.filter((x) => !bonnes.some((b) => isEquivalent(b, x)));
    while (dist2.length < 3) {
        const alt = shiftRight(q1, dist2.length + 1);
        if (!bonnes.some((b) => isEquivalent(b, alt)) && !dist2.includes(alt)) {
            dist2.push(alt);
        } else break;
    }

    const payload = makeTexteTrou(
        phrase,
        `${RAPPEL_BROUILLON}<p>Complète la cascade de divisions par ${d} en faisant glisser les bons quotients.</p>`,
        { ...opts(L_DRAG), mode: 'drag', distracteurs: dist2.slice(0, 3), strict: false },
    );
    payload._meta.generatorKey = 'drag_cascade';
    return payload;
};

const buildDragCascadeFallback = () => {
    const payload = makeTexteTrou(
        `15 ÷ 4 = {{3,75}}\n19 ÷ 4 = {{4,75}}\n27 ÷ 4 = {{6,75}}`,
        `${RAPPEL_BROUILLON}<p>Complète la cascade de divisions par 4 en faisant glisser les bons quotients.</p>`,
        { ...opts(L_DRAG), mode: 'drag', distracteurs: ['37,5', '0,475', '6,57'], strict: false },
    );
    payload._meta.generatorKey = 'drag_cascade';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRE
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    quotient_calc_qcm_unique:       genQuotientCalcQcm,
    quotient_calc_texte:            genQuotientCalcSaisie,
    prix_unit_direct_qcm_unique:    genPrixUnitDirectQcm,
    prix_unit_direct_texte:         genPrixUnitDirectSaisie,
    vrai_faux_divisions_tableau_vf: genVraiFauxDivisionsTableau,
    drag_cascade_texte_trou_drag:   genDragCascadeTrou,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', relier: 'Relier', tableau_vf: 'Vrai/Faux',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'quotient_calc',          label: "Calcul direct d'un quotient décimal (ou moitié d'un décimal)", formatsList: buildFormats('quotient_calc') },
    { id: 'prix_unit_direct',       label: 'Problème prix unitaire direct',                                formatsList: buildFormats('prix_unit_direct') },
    { id: 'vrai_faux_divisions',    label: L_VF,                                                           formatsList: buildFormats('vrai_faux_divisions') },
    { id: 'drag_cascade',           label: L_DRAG,                                                         formatsList: buildFormats('drag_cascade') },
];

// ─── Point d'entrée ───
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) =>
            k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    // Toujours forcer la clé complète (les générateurs ne posent que le préfixe
    // catégorie ce qui cassait le "regenerate dans le même format")
    payload._meta.generatorKey = key;
    return payload;
};
